
import Vue from "vue";
import { TypedVue } from "@/store/types";
import { Component, Watch, Ref, Prop } from "vue-property-decorator";
import { Modal } from "bootstrap";
import { V2Assessment, V2Project, V2Benchmark } from "@/store/admin/adminTypes";
import { systemAPI } from "@/lib/s3PlatformApi";
import { Trait, TRAIT_GROUPINGS, TRAITS, TAG_COLOURS } from "@/lib/consts";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem, ComparisonChartPreset } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import { AssessmentState, CreateProjectReport, SchemasCandidateAssessment, SchemasV2InternalAssessmentsScoring } from "@/lib/serviceTypes";
import { Benchmark, ProjectReportType } from "@/store/internal/internalTypes";
import { Benchmark as AdminSchemaBenchmark, EvaluationReportData, ProjectReportData } from "@/store/admin/adminTypes";
import { ColorMap } from "@/lib/charts/HighChartThemes";
import draggable from "vuedraggable";
import moment from "moment-mini";
import { Action, Getter } from "vuex-class";
import { Actions } from "@/store/admin/adminActions";
import { Actions as InternalActions } from "@/store/internal/interalActions";
import TagPicker from "@/components/input/TagPicker.vue";
import ComparisonColourPickerModal from "@/components/admin/ComparisonComponents/ComparisonColourPickerModal.vue";
import { Getters } from "@/store/admin/adminGetters";
import { Roles } from "@/lib/auth/User";

@Component({ components: { ComparisonRadarChartComponent, draggable, TagPicker, ComparisonColourPickerModal } })
export default class EvaluationReportWizardModal extends TypedVue {
  @Ref("add-project-report-modal")
  readonly modal!: Modal;

  @Prop() show!: boolean;
  @Prop({ default: null }) project!: V2Project;
  @Prop({ default: null }) reportToEdit!: string | null;
  @Prop({ default: false }) enableFourCompetencyGrouping!: boolean;

  @Action(Actions.SAVE_PROJECT_REPORT, { namespace: "admin" })
  systemSaveProjectReport!: (reportArgs: { projectId: string; projectReport: CreateProjectReport }) => Promise<string>;

  @Action(InternalActions.UPDATE_REPORT, { namespace: "internal" })
  updateProjectReport!: (data: { reportId: string; name: string; description: string | undefined; assessmentIds: string[]; data: Record<string, unknown> }) => Promise<void>;

  @Action(Actions.GET_ORG_PROJECTS, { namespace: "admin" })
  getProjects!: () => Promise<V2Project[]>;

  @Action(Actions.GET_PROJECT_REPORT_DATA, { namespace: "admin" })
  getProjectReportData!: ({ reportId, raw }: { reportId: string; raw?: boolean }) => Promise<ProjectReportData>;

  @Getter(Getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID, { namespace: "admin" })
  public rootBenchmarkId!: string;

  @Watch("show")
  async onShowChange(show: boolean) {
    if (show) {
      this.modal.show();

      if (this.project) {
        // Get initial data
        this.assessmentsLoading = true;
        const assessmentsPromise = this.getAssessments().then(() => {
          this.setComparableAssessments();
        });
        const benchmarkPromise = this.getBenchmarks(this.project.identifier).then(() => {
          this.compariableBenchmarks = this.benchmarks.map((benchmark) => ({
            id: benchmark.identifier,
            name: benchmark.name,
            hidden: false,
            isLocked: false,
            selected: benchmark.default || false,
            color: benchmark.colour,
            initialColor: benchmark.colour,
            data: this.mapBenchmarkToSchemaBenchmark(benchmark),
          }));
        });

        const tagsPromise = this.getTags();

        // If editing an existing report, load its data
        let reportPromise: Promise<void> | null = null;
        if (this.reportToEdit) {
          this.reportLoading = true;
          this.editMode = true;
          reportPromise = this.getProjectReportData({ reportId: this.reportToEdit, raw: true }).then((report) => {
            this.initialReport = {
              identifier: report.identifier,
              name: report.name,
              description: report.description,
              version: report.version,
              type: report.type,
              assessmentIds: report.assessmentIds || [],
              data: report.data as EvaluationReportData,
            };
          });
          await Promise.all([assessmentsPromise, benchmarkPromise, tagsPromise, reportPromise])
            .then(() => {
              this.populateInitialReportData();
            })
            .finally(() => {
              this.reportLoading = false;
              this.assessmentsLoading = false;
            });
        } else {
          this.neutralCompetencies = [];
          for (const trait of Object.values(TRAITS)) {
            this.neutralCompetencies.push(trait);
          }
          await Promise.all([assessmentsPromise, benchmarkPromise, tagsPromise]).finally(() => {
            this.assessmentsLoading = false;
          });
        }
      }
    } else {
      this.modal.hide();
      this.resetState();
    }
  }

  @Watch("selectedAssessmentIds")
  onSelectedAssessmentIdsChange(newValue: string[], oldValue: string[]) {
    this.setComparableAssessments();

    if (newValue.length < oldValue.length) {
      const removedAssessmentIds = oldValue.filter((x) => !newValue.includes(x));
      for (const removedAssessmentId of removedAssessmentIds) {
        for (const preset of this.comparisonPresets) {
          preset.items = preset.items.filter((x) => x.id !== removedAssessmentId);
        }

        const presetToRemove = this.comparisonPresets.find((x) => x.id === `insight-preset-${removedAssessmentId}`);
        if (presetToRemove) {
          this.removePreset(presetToRemove);
        }
      }
    }

    if (this.userIsGlobalAdmin) {
      if (newValue.length > oldValue.length && !this.settingInitialValues) {
        const addedAssessmentIds = newValue.filter((x) => !oldValue.includes(x));
        for (const addedAssessmentId of addedAssessmentIds) {
          if (!this.comparisonPresets.find((x) => x.id === `insight-preset-${addedAssessmentId}`)) {
            this.getExecutiveSummary(addedAssessmentId).then((summary) => {
              this.$set(this.executiveSummaries, addedAssessmentId, summary);
              this.createPresetForNewAssessment(addedAssessmentId, summary);
            });
          }
        }
      }
    }
  }

  @Watch("tags", { deep: true })
  onTagsChange(newVal: Record<string, string[]>) {
    if (this.settingInitialValues) return;
    for (const tagList of Object.values(newVal)) {
      for (const tag of tagList) {
        if (!this.tagColours[tag]) {
          this.tagColours[tag] = TAG_COLOURS[Object.keys(this.tagColours).length % TAG_COLOURS.length];
          this.showColourPicker(tag);
        }
      }
    }
  }

  public step = 1;
  public totalSteps = 5;
  public editMode = false;
  public scoringVerison = "v2";
  public reportVersion = "1.0";
  public reportType: ProjectReportType = "evaluation-report";
  public initialReport: {
    identifier: string;
    name: string;
    description?: string;
    version: string;
    type: string;
    assessmentIds?: string[];
    data: EvaluationReportData;
  } | null = null;
  public assessments: V2Assessment[] = [];
  public benchmarks: V2Benchmark[] = [];
  public candidateSelectedState: Record<string, boolean> = {};
  public titleChoices = [
    { value: "candidate", text: "Candidate" },
    { value: "team", text: "Team" },
    { value: "manager", text: "Manager" },
  ];

  searchText = "";
  reportName = "";
  reportDescription = "";
  candidateInsightsCustomText: Record<string, string> = {};
  executiveSummaries: Record<string, string> = {};
  system3InsightsCustomText: Record<string, string> = {};
  searchPartnerInsightsCustomText: Record<string, string> = {};
  executiveSummaryText = "";

  participantTitles: { [key: string]: string } = {};
  tags: Record<string, string[]> = {};
  tagColours: Record<string, string> = {};
  tagOptions: string[] = [];
  settingInitialValues = false;
  showColourPickerModal = false;
  selectedColourItem: string | null = null;

  mustHaveCompetencies: Trait[] = [];
  niceToHaveCompetencies: Trait[] = [];
  neutralCompetencies: Trait[] = [];

  compariableBenchmarks = Vue.observable<ComparisonBenchmarkItem[]>([]);
  compariableAssessments = Vue.observable<ComparisonAssessmentItem[]>([]);
  comparisonPresets = Vue.observable<ComparisonChartPreset[]>([]);

  generatedReportId: string | null = null;
  assessmentsLoading = false;
  reportLoading = false;
  saving = false;

  get stepName() {
    switch (this.step) {
      case 1:
        return "Name Your Report";
      case 2:
        return "Add Participants";
      case 3:
        return "Customize Competency Priorities";
      case 4:
        return "Add Candidate Insights";
      case 5:
        return this.editMode ? "Review & Update Report" : "Review & Confirm";
      default:
        return "";
    }
  }

  get canProceedToNextStep() {
    if (this.step === 1) {
      return this.reportName.trim().length > 0;
    } else if (this.step === 2) {
      return this.selectedCount > 0;
    } else if (this.step === 3) {
      return this.mustHaveCompetencies.length === 5 && this.niceToHaveCompetencies.length === 5 && this.neutralCompetencies.length === 5;
    }
    return true;
  }

  get validAssessments() {
    return this.assessments
      .filter((assessment) => {
        if (!assessment.report) {
          return false;
        }
        if (!assessment.scores?.find((x) => x.scoringVersion === this.scoringVerison)) {
          return false;
        }
        if (this.searchText) {
          return assessment.candidate.name.toLowerCase().includes(this.searchText.toLowerCase()) || assessment.identifier.slice(0, 4).toLowerCase().includes(this.searchText.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => {
        const aName = a.candidate.name.toLowerCase();
        const bName = b.candidate.name.toLowerCase();
        return aName.localeCompare(bName);
      });
  }

  get selectedAssessmentIds() {
    return Object.keys(this.candidateSelectedState).filter((key) => this.candidateSelectedState[key]);
  }

  set selectedAssessmentIds(value: string[]) {
    this.candidateSelectedState = this.assessments.reduce((acc, assessment) => {
      acc[assessment.identifier] = value.includes(assessment.identifier);
      return acc;
    }, {} as Record<string, boolean>);

    this.participantTitles = this.assessments.reduce((acc, assessment) => {
      if (this.initialReport?.data.managers.includes(assessment.identifier)) {
        acc[assessment.identifier] = "manager";
      } else if (this.initialReport?.data.team.includes(assessment.identifier)) {
        acc[assessment.identifier] = "team";
      } else {
        acc[assessment.identifier] = "candidate";
      }
      return acc;
    }, {} as Record<string, string>);
  }

  get selectedCount() {
    return Object.values(this.candidateSelectedState).filter((selected) => selected).length;
  }

  get selectedAssessments() {
    return this.assessments
      .filter((assessment) => this.candidateSelectedState[assessment.identifier])
      .sort((a, b) => {
        const aName = a.candidate.name.toLowerCase();
        const bName = b.candidate.name.toLowerCase();
        return aName.localeCompare(bName);
      });
  }

  get selectedCandidates() {
    return this.selectedAssessments
      .filter((assessment) => Object.keys(this.participantTitles).includes(assessment.identifier) && this.participantTitles[assessment.identifier] === "candidate")
      .sort((a, b) => {
        const aName = a.candidate.name.toLowerCase();
        const bName = b.candidate.name.toLowerCase();
        return aName.localeCompare(bName);
      });
  }

  get selectedBenchmarks() {
    return this.compariableBenchmarks.filter((benchmark) => benchmark.selected);
  }

  get allTags() {
    return Array.from(new Set(Object.values(this.tags).flat().concat(this.tagOptions)));
  }

  get candidateInsightsCount() {
    const ids = new Set([...Object.keys(this.system3InsightsCustomText), ...Object.keys(this.searchPartnerInsightsCustomText)]);
    return [...ids].filter((id) => this.system3InsightsCustomText[id]?.trim() || this.searchPartnerInsightsCustomText[id]?.trim()).length;
  }

  get userIsGlobalAdmin(): boolean {
    const user = this.$auth.user;
    if (user) {
      return user.hasRole(Roles.GLOBAL_ADMIN);
    }
    return false;
  }

  // Mirror the report's inline-editing gating (AnalysisTab): System-3 Insights are
  // GlobalAdmin-only; Search Partner Insights are editable by Global or Organization admins.
  get canEditSystem3Insights(): boolean {
    return this.userIsGlobalAdmin;
  }

  get canEditSearchPartnerInsights(): boolean {
    return this.userIsGlobalAdmin || this.$auth.user?.hasRole(Roles.ORGANIZATION_ADMIN) || false;
  }

  categoryFor(trait: Trait) {
    return Object.keys(TRAIT_GROUPINGS).find((group) => TRAIT_GROUPINGS[group].includes(trait));
  }

  nextStep(): void {
    if (this.step === this.totalSteps) {
      this.saveReport().catch((error) => {
        console.error("Error saving report:", error);
      });
    }

    if (this.step < this.totalSteps) this.step++;
  }

  prevStep(): void {
    if (this.step > 1) this.step--;
  }

  removePreset(preset: ComparisonChartPreset) {
    const index = this.comparisonPresets.findIndex((x) => x.id === preset.id);
    if (index !== -1) {
      this.comparisonPresets.splice(index, 1);
    }
  }

  savePreset(preset: ComparisonChartPreset) {
    this.comparisonPresets.push(preset);
  }

  updatePreset(preset: ComparisonChartPreset) {
    const index = this.comparisonPresets.findIndex((x) => x.id === preset.id);
    if (index !== -1) {
      this.comparisonPresets.splice(index, 1, preset);
    }

    if (preset.id.startsWith("insight-preset-")) {
      const assessmentId = preset.id.replace("insight-preset-", "");
      this.executiveSummaries[assessmentId] = preset.description;
    }
  }

  async getAssessments() {
    this.assessments = (await systemAPI.getAssessments({ include_scores: true })).data;
  }

  async getBenchmarks(projectId: string) {
    this.benchmarks = (await systemAPI.getBenchmarks({ project_id: projectId })).data;
  }

  async getTags() {
    this.tagOptions = (await systemAPI.getReportTags("search-report")).data;
  }

  async getExecutiveSummary(assessmentId: string) {
    if (this.executiveSummaries[assessmentId]) {
      return this.executiveSummaries[assessmentId];
    }

    const response = await systemAPI.getAssessmentInsight(assessmentId);
    const insight = response.data.insight;
    if (insight && typeof insight === "object") {
      return Object.entries(insight)
        .map(([key, value]) => `• ${key}: ${value}`)
        .join(" ");
    }
    return insight;
  }

  public createPresetForNewAssessment(assessmentId: string, executiveSummary: string) {
    const assessment = this.compariableAssessments.find((x) => x.id === assessmentId);
    if (!assessment) return;

    const benchmark = this.compariableBenchmarks.find((x) => x.id === this.rootBenchmarkId);
    if (!benchmark) return;

    const preset: ComparisonChartPreset = {
      id: `insight-preset-${assessment.id}`,
      name: assessment.data.candidateName,
      description: executiveSummary,
      items: [assessment, benchmark],
    };

    this.comparisonPresets.push(preset);
  }

  public setComparableAssessments() {
    this.compariableAssessments = this.assessments
      .filter((assessment) => this.selectedAssessmentIds.includes(assessment.identifier))
      .filter((assessment) => assessment.scores?.find((x) => x.scoringVersion === "v2"))
      .map((assessment) => ({ assessment, scoring: assessment.scores?.find((x) => x.scoringVersion === "v2") as SchemasV2InternalAssessmentsScoring }))
      .map(({ assessment, scoring }) => ({
        id: assessment.identifier,
        name: `${assessment.identifier.substring(0, 4)} - ${assessment.candidate.name}`,
        hidden: false,
        isLocked: false,
        selected: false,
        color: ColorMap[this.project.theme][scoring.readiness],
        initialColor: ColorMap[this.project.theme][scoring.readiness],
        data: this.mapAssessmentToSchemaAssessment(assessment, scoring),
      }));
  }

  public populateInitialReportData() {
    if (this.initialReport) {
      this.settingInitialValues = true;

      this.editMode = true;
      this.reportName = this.initialReport.name;
      this.reportDescription = this.initialReport.description ?? "";
      this.selectedAssessmentIds = this.initialReport.assessmentIds ?? [];

      this.mustHaveCompetencies = Object.entries(this.initialReport.data.traitPriorities)
        .filter(([, value]) => value === 2)
        .map((x) => x[0] as Trait);
      this.niceToHaveCompetencies = Object.entries(this.initialReport.data.traitPriorities)
        .filter(([, value]) => value === 1)
        .map((x) => x[0] as Trait);
      this.neutralCompetencies = Object.entries(this.initialReport.data.traitPriorities)
        .filter(([, value]) => value === 0)
        .map((x) => x[0] as Trait);
      this.tags = this.initialReport.data.tags || {};
      this.tagColours = this.initialReport.data.tagColours || {};
      this.candidateInsightsCustomText = this.initialReport.data.customText.candidates || {};
      this.executiveSummaries = this.initialReport.data.executiveSummaries || {};
      this.system3InsightsCustomText = this.initialReport.data.customText.system3Insights || {};
      this.searchPartnerInsightsCustomText = this.initialReport.data.customText.searchPartnerInsights || {};
      this.executiveSummaryText = this.initialReport.data.customText.executiveSummary || "";

      // Use nextTick to ensure all reactive updates are complete
      this.$nextTick(() => {
        this.settingInitialValues = false;
      });
    }
  }

  async saveReport() {
    this.saving = true;

    if (this.editMode) {
      await this.updateProjectReport({
        reportId: this.initialReport?.identifier || "",
        name: this.reportName,
        description: this.reportDescription,
        assessmentIds: this.selectedAssessmentIds,
        data: this.createReportData(),
      });
      this.generatedReportId = this.initialReport?.identifier || "";
    } else {
      const projectReport = {
        name: this.reportName,
        description: this.reportDescription,
        reportVersion: this.reportVersion,
        reportType: this.reportType,
        scoringVersion: this.scoringVerison,
        assessmentIds: this.selectedAssessmentIds,
        data: this.createReportData(),
      };
      this.generatedReportId = await this.systemSaveProjectReport({
        projectId: this.project.identifier,
        projectReport,
      });
    }

    await this.getProjects().finally(() => {
      this.saving = false;
      this.close();
    });
  }

  createReportData() {
    const traitWeights = {} as Record<string, number>;
    Object.values(TRAITS).forEach((trait) => (traitWeights[trait] = 3));

    const traitPriorities = {} as Record<string, number>;
    this.mustHaveCompetencies.forEach((trait) => (traitPriorities[trait] = 2));
    this.niceToHaveCompetencies.forEach((trait) => (traitPriorities[trait] = 1));
    this.neutralCompetencies.forEach((trait) => (traitPriorities[trait] = 0));

    return {
      traitPriorities: traitPriorities,
      traitWeights: traitWeights,
      candidates: Object.entries(this.participantTitles)
        .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "candidate")
        .map((x) => x[0]),
      team: Object.entries(this.participantTitles)
        .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "team")
        .map((x) => x[0]),
      managers: Object.entries(this.participantTitles)
        .filter(([key, value]) => this.selectedAssessmentIds.includes(key) && value === "manager")
        .map((x) => x[0]),
      tags: Object.keys(this.tags).reduce((acc, assessment) => {
        if (this.selectedAssessmentIds.includes(assessment)) {
          acc[assessment] = this.tags[assessment];
        }
        return acc;
      }, {} as Record<string, string[]>),
      tagColours: this.tagColours,
      comparisonPresets: this.comparisonPresets,
      customText: {
        executiveSummary: this.executiveSummaryText,
        candidates: this.candidateInsightsCustomText,
        // Per-candidate insights entered in the wizard's insights step. These are the same
        // fields the report's Candidate profile tab edits inline, so loading (populateInitialReportData)
        // and re-saving through the wizard preserves any inline edits. Drop entries for de-selected
        // participants so removing someone clears their insights too.
        system3Insights: this.filterInsightsToSelectedAssessments(this.system3InsightsCustomText),
        searchPartnerInsights: this.filterInsightsToSelectedAssessments(this.searchPartnerInsightsCustomText),
      },
      candidateRoles: this.initialReport?.data.candidateRoles ?? {},
      executiveSummaries: this.executiveSummaries,
    };
  }

  filterInsightsToSelectedAssessments(insights: Record<string, string>): Record<string, string> {
    return Object.entries(insights)
      .filter(([assessmentId, text]) => this.selectedAssessmentIds.includes(assessmentId) && text?.trim())
      .reduce((acc, [assessmentId, text]) => {
        acc[assessmentId] = text;
        return acc;
      }, {} as Record<string, string>);
  }

  mapAssessmentToSchemaAssessment(assessment: V2Assessment, score: SchemasV2InternalAssessmentsScoring): SchemasCandidateAssessment {
    return {
      assessmentId: assessment.identifier,
      candidateName: assessment.candidate.name,
      isComplete: assessment.state === AssessmentState.ScoringComplete,
      project: assessment.projectId,
      organization: assessment.company,
      competencyScores: score.competencyScores,
      competencies: score.competencies,
      traitScores: score.traitScores,
      readinessScore: score.readiness,
      dateCompleted: assessment.dateCompleted,
    }; // TODO: We need to migrate the comparison radar chart to use the new schema and we can remove this
  }

  mapBenchmarkToSchemaBenchmark(benchmark: Benchmark): AdminSchemaBenchmark {
    return {
      alwaysShow: benchmark.default,
      benchmarkId: benchmark.identifier,
      colour: benchmark.colour,
      name: benchmark.name,
      competencyScores: benchmark.competencyScores,
      isAdmin: benchmark.isAdmin,
      organization: benchmark.orgId,
      traitScores: benchmark.traitScores,
      traitRanges: benchmark.traitRanges,
      dateCreated: "",
      lastUpdated: "",
    }; // TODO: We need to migrate the comparison radar chart to use the new schema and we can remove this
  }

  public colourDialogComplete(color: string): void {
    this.showColourPickerModal = false;
    if (this.selectedColourItem) {
      Vue.set(this.tagColours, this.selectedColourItem, color);
      Vue.nextTick(() => {
        this.tagColours = { ...this.tagColours }; // Force reactivity
      });
    }
  }

  public colourDialogClose(): void {
    this.showColourPickerModal = false;
  }

  public colourDialogReset(): void {
    if (this.selectedColourItem) {
      Vue.delete(this.tagColours, this.selectedColourItem);
    }

    this.showColourPickerModal = false;
  }

  public showColourPicker(item: string): void {
    this.selectedColourItem = item;
    this.showColourPickerModal = true;
  }

  toFriendlyDate(date: string): string {
    const mDate = moment.utc(date).local();
    return mDate.format("MM/DD/YYYY");
  }

  close(): void {
    this.$emit("close");
    this.resetState();
  }

  resetState(): void {
    this.step = 1;
    this.editMode = false;
    this.reportName = "";
    this.reportDescription = "";
    this.searchText = "";
    this.candidateSelectedState = {};
    this.participantTitles = {};
    this.tags = {};
    this.mustHaveCompetencies = [];
    this.niceToHaveCompetencies = [];
    this.neutralCompetencies = [];
    this.compariableBenchmarks = [];
    this.compariableAssessments = [];
    this.comparisonPresets = [];
    this.executiveSummaryText = "";
    this.candidateInsightsCustomText = {};
    this.system3InsightsCustomText = {};
    this.searchPartnerInsightsCustomText = {};
    this.executiveSummaries = {};
    for (const trait of Object.values(TRAITS)) {
      this.neutralCompetencies.push(trait);
    }
  }
}
